import React from 'react';
import { Checkboxes, ErrorMessage } from 'nhsuk-react-components';
import { CognitoUser, UserInfo } from 'schemas/UserInfo.schema.ts';
import ProgressIndicator from 'components/ProgressIndicator';
import useTeamReviewers from 'hooks/useTeamReviewers.ts';

interface IReviewerPickerProps {
  content: React.JSX.Element,
  teamId: string,
  iterationCreator: UserInfo,
  selectedReviewers: CognitoUser[],
  setSelectedReviewers: (reviewers: CognitoUser[]) => void,
}
function ReviewerPicker({
  content,
  teamId,
  iterationCreator,
  selectedReviewers,
  setSelectedReviewers,
}: IReviewerPickerProps) {
  const { possibleReviewers, isLoading, error } = useTeamReviewers(teamId, iterationCreator);

  if (isLoading) {
    return <ProgressIndicator text="Loading reviewers..." />;
  }

  return (
    <>
      {content}
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
      <form>
        {possibleReviewers.length > 0 ? (
          <>
            <p>
              Choose one or more reviewers:
            </p>
            <Checkboxes name="reviewers">
              {possibleReviewers
                .toSorted((a, b) => (a.given_name || '').localeCompare(b.given_name || ''))
                .map((reviewer) => (
                  <Checkboxes.Item
                    key={reviewer.sub}
                    value={reviewer.sub}
                    checked={selectedReviewers.includes(reviewer)}
                    onChange={() => {
                      if (selectedReviewers.includes(reviewer)) {
                        setSelectedReviewers([
                          ...selectedReviewers.filter(
                            (r) => r.sub !== reviewer.sub,
                          ),
                        ]);
                        return;
                      }
                      setSelectedReviewers([...selectedReviewers, reviewer]);
                    }}
                  >
                    {reviewer.given_name && reviewer.family_name
                      ? `${reviewer.given_name} ${reviewer.family_name}`
                      : reviewer.email || 'Unnamed user'}
                  </Checkboxes.Item>
                ))}
            </Checkboxes>
          </>
        ) : (
          <p className="nhsuk-body nhsuk-u-font-weight-bold">
            There are no users in team&nbsp;
            {teamId}
            {' '}
            with the reviewer role. Please contact your SPI administrator to add reviewers to the team.
          </p>
        )}
      </form>
    </>
  );
}
export default ReviewerPicker;
